import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Flower2, Gift, Printer, Sparkles, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { GARDIENS, gardienById, PRODUCT_GARDIENS, APP_UNIVERS, COLLECTIONS } from "@/data/gardiens";
import { PILL_BTN } from "@/lib/pill";
import { downloadGardienCard, downloadFamilierCard } from "@/lib/gardienCard";
import { api } from "@/lib/api";

const GardienPage = () => {
  const { id } = useParams();
  const g = gardienById(id);
  const [products, setProducts] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printingFam, setPrintingFam] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const fetiche = useMemo(() => {
    return products.find((p) => (p.gardien || PRODUCT_GARDIENS[p.slug]) === id) || null;
  }, [products, id]);

  if (!g) return <Navigate to="/gardiens" replace />;

  const collection = COLLECTIONS.find((c) => c.id === g.collection);
  const univers = APP_UNIVERS.filter((u) => u.gardienIds.includes(g.id));
  const autres = GARDIENS.filter((x) => x.collection === g.collection && x.id !== g.id).slice(0, 3);

  const printCard = async () => {
    setPrinting(true);
    try { await downloadGardienCard(g); } catch { toast.error("La carte n'a pas pu être créée."); }
    setPrinting(false);
  };

  const printFamilier = async () => {
    setPrintingFam(true);
    try { await downloadFamilierCard(g); } catch { toast.error("La carte n'a pas pu être créée."); }
    setPrintingFam(false);
  };

  return (
    <div className="pt-32 pb-24" data-testid="gardien-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10">
        <Link to="/gardiens" data-testid="gardien-page-back" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4" /> Tous les Gardiens
        </Link>

        {/* EN-TÊTE */}
        <Reveal>
          <div className="mt-10 flex flex-col sm:flex-row items-center sm:items-start gap-10">
            <div className="relative shrink-0 w-52 h-52">
              <div className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 5px ${g.color}44, 0 0 60px ${g.color}33` }} />
              <img src={g.image} alt={`${g.name} — ${g.title}`} className="w-52 h-52 rounded-full object-cover object-top" data-testid="gardien-page-image" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-sm uppercase tracking-[0.3em] text-warm">{collection?.label}</p>
              <h1 className="font-serif-display italic text-5xl sm:text-6xl font-light mt-3" data-testid="gardien-page-name">{g.name}</h1>
              <p className="text-lg text-muted-foreground mt-2">{g.title}</p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em]" style={{ color: g.color }} data-testid="gardien-page-value">{g.value}</p>
              <p className="font-serif-display italic text-2xl text-foreground/80 mt-5 max-w-lg leading-relaxed">« {g.quote} »</p>
            </div>
          </div>
        </Reveal>

        {/* HISTOIRE */}
        <Reveal delay={0.1}>
          <div className="mt-14 bg-card rounded-[2rem] border border-warm/25 p-8 sm:p-12 grain">
            <h2 className="font-serif-display italic text-3xl sm:text-4xl font-light">Son histoire</h2>
            <p className="mt-6 text-lg text-foreground/75 leading-relaxed" data-testid="gardien-page-story">{g.story}</p>
          </div>
        </Reveal>

        {/* FIDÈLE COMPAGNON */}
        {g.familiar && (
          <Reveal delay={0.12}>
            <div className="mt-8 bg-card rounded-[2rem] border border-warm/25 p-8 sm:p-10 grain" data-testid="gardien-page-familiar">
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <div className="relative shrink-0 w-36 h-36">
                  <div className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 4px ${g.color}33, 0 0 0 8px ${g.color}1A` }} />
                  <img src={g.familiar.image} alt={`${g.familiar.name} — ${g.familiar.title}`} className="w-36 h-36 rounded-full object-cover" data-testid="gardien-page-familiar-image" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm uppercase tracking-[0.3em] text-warm">Son fidèle compagnon</p>
                  <h2 className="font-serif-display italic text-3xl sm:text-4xl font-light mt-2" data-testid="gardien-page-familiar-name">{g.familiar.name}</h2>
                  <p className="text-lg text-muted-foreground mt-1">{g.familiar.title}</p>
                  <p className="text-foreground/70 leading-relaxed mt-4 max-w-lg" data-testid="gardien-page-familiar-story">{g.familiar.story || `Inséparables, ${g.familiar.name} et ${g.name} veillent ensemble sur chaque émotion précieuse, de jour comme de nuit.`}</p>
                  <button onClick={printFamilier} disabled={printingFam} data-testid="gardien-page-familiar-print-btn" className="mt-5 inline-flex items-center gap-2 rounded-full border border-warm/50 text-warm px-6 py-2.5 text-sm hover:bg-warm/10 transition-colors disabled:opacity-60">
                    <Printer className="w-4 h-4" /> {printingFam ? "Création de la carte…" : "Imprimer sa carte"}
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {/* CHEMIN DE SOIN */}
        <Reveal delay={0.14}>
          <div className="mt-8 bg-card rounded-[2rem] border border-warm/25 p-8 sm:p-10" data-testid="gardien-page-care">
            <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Son chemin de soin</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Pathologie</p>
                <p className="font-serif-display text-2xl mt-1.5" data-testid="gardien-page-pathologie">{g.pathologie}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Thérapie douce</p>
                <p className="font-serif-display text-2xl mt-1.5" data-testid="gardien-page-therapie">{g.therapie}</p>
              </div>
            </div>
            <Link to={`/pathologies#pathologie-${g.pathoRef}`} data-testid="gardien-page-patho-link"
              className="group inline-flex items-center gap-2 mt-7 text-sm text-warm hover:text-primary transition-colors">
              Comprendre cette pathologie et ses approches <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>

        {/* ACTIVITÉS */}
        <Reveal delay={0.15}>
          <div className="mt-8 bg-card rounded-[2rem] border border-border/50 p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Flower2 className="w-6 h-6 text-warm" strokeWidth={1.5} />
              <h2 className="font-serif-display text-2xl sm:text-3xl">Ses activités douces</h2>
            </div>
            <ul className="space-y-4">
              {g.activities.map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-foreground/80 leading-relaxed" data-testid={`gardien-page-activity-${i}`}>
                  <Check className="w-5 h-5 text-warm shrink-0 mt-0.5" /> {a}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* UNIVERS DE L'APPLICATION */}
        {univers.length > 0 && (
          <Reveal delay={0.2}>
            <div className="mt-8 bg-card rounded-[2rem] border border-border/50 p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-5">
                <Gamepad2 className="w-6 h-6 text-warm" strokeWidth={1.5} />
                <h2 className="font-serif-display text-2xl sm:text-3xl">Dans l'application</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                {univers.map((u) => (
                  <span key={u.label} data-testid={`gardien-page-univers-${u.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full bg-warm/10 border border-warm/30 text-sm px-5 py-2.5 text-foreground/80">
                    {u.label}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {/* PRODUIT FÉTICHE */}
        {fetiche && (
          <Reveal delay={0.25}>
            <div className="mt-8 bg-card rounded-[2rem] border border-border/50 p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-6 h-6 text-warm" strokeWidth={1.5} />
                <h2 className="font-serif-display text-2xl sm:text-3xl">Son objet fétiche</h2>
              </div>
              <Link to="/boutique" data-testid="gardien-page-product" className="group flex flex-col sm:flex-row gap-6 items-center bg-background rounded-2xl border border-border/50 p-5 hover:-translate-y-1 hover:shadow-[0_16px_30px_rgb(0,0,0,0.08)] transition-all duration-300">
                <img src={fetiche.image} alt={fetiche.name} className="w-32 h-32 rounded-2xl object-cover shrink-0" />
                <div className="text-center sm:text-left">
                  <p className="font-medium text-lg leading-snug group-hover:text-primary transition-colors">{fetiche.name}</p>
                  <p className="text-sm text-foreground/60 mt-2 leading-relaxed">{fetiche.description}</p>
                  <p className="font-serif-display text-2xl mt-3">{fetiche.price.toFixed(0)} €</p>
                </div>
              </Link>
            </div>
          </Reveal>
        )}

        {/* OBJETS SUR MESURE — encart spécial Émile */}
        {g.id === "emile" && (
          <Reveal delay={0.28}>
            <div className="mt-8 rounded-[2rem] border p-8 sm:p-10 grain" style={{ backgroundColor: `${g.color}12`, borderColor: `${g.color}40` }} data-testid="gardien-page-sur-mesure">
              <p className="text-sm uppercase tracking-[0.3em]" style={{ color: g.color }}>Objets sur mesure</p>
              <h2 className="font-serif-display italic text-3xl sm:text-4xl font-light mt-2">Créations personnalisées avec Émile</h2>
              <p className="mt-4 text-foreground/75 leading-relaxed max-w-2xl">
                Émile veille aussi sur notre atelier d'adaptation : nous créons des objets ergonomiques pour les hobbies de votre proche — couture, peinture, alimentation et bien d'autres — et pour sa morphologie ou son handicap. Le projet se co-construit avec vous par email, chaque étape vous est partagée en temps réel, et les matériaux sont garantis résistants ou aptes au contact alimentaire selon l'usage.
              </p>
              <Link to="/#creations-emile" data-testid="gardien-page-sur-mesure-link" className={`mt-6 ${PILL_BTN}`}>
                Découvrir les objets sur mesure <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        )}

        {/* ACTIONS */}
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/quiz-gardien" data-testid="gardien-page-quiz-btn" className={PILL_BTN}>
              <Sparkles className="w-4 h-4" /> Trouver le Gardien de votre proche <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button onClick={printCard} disabled={printing} data-testid="gardien-page-print-btn" className="inline-flex items-center gap-2 rounded-full border border-warm/50 text-warm px-8 py-4 hover:bg-warm/10 transition-colors disabled:opacity-60">
              <Printer className="w-4 h-4" /> {printing ? "Création de la carte…" : "Imprimer sa carte"}
            </button>
          </div>
        </Reveal>

        {/* AUTRES GARDIENS */}
        <div className="mt-20">
          <h2 className="font-serif-display italic text-3xl sm:text-4xl font-light text-center">Découvrir aussi</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-10">
            {autres.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <Link to={`/gardiens/${a.id}`} data-testid={`gardien-page-other-${a.id}`} className="group block bg-card rounded-[2rem] border border-warm/25 p-7 text-center h-full hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgb(0,0,0,0.10)] transition-all duration-500">
                  <img src={a.image} alt={`${a.name} — ${a.title}`} className="w-24 h-24 rounded-full object-cover object-top mx-auto group-hover:scale-105 transition-transform duration-700" />
                  <p className="mt-4 text-xs uppercase tracking-[0.25em]" style={{ color: a.color }}>{a.value}</p>
                  <p className="font-serif-display text-2xl mt-1.5">{a.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">{a.title}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GardienPage;
